// PORTAIS (2026-09-03) — wrappers das actions por_*.
//
// Onde a Hydrodata está cadastrada como fornecedora: portais de cliente
// (Solví, Copasa, Samarco, Aegea, Auren) e de licitação (BLL, BNC, E-lic).
//
// ⚠️ Mesma rota do cofre e das outras ~400 actions:
// `POST /comercial/propostas-geradas`, o roteador genérico por `body.action`.
// Não é inconsistência gratuita — criar recurso novo no API Gateway é ritual
// manual (e estamos no limite de 500 recursos), e a rota é só transporte.
//
// 🔒 Duas ausências deliberadas no tipo `Portal`:
//    • a SENHA, que só existe na resposta de `revelarSenha`, uma vez, e essa
//      chamada fica registrada no servidor antes de responder;
//    • o `segredo_id`. O navegador não recebe o identificador do segredo —
//      `revelarSenha` manda o id do PORTAL e o servidor resolve o resto. Sem
//      o id, não há o que apontar para a senha do certificado digital.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

export type TipoPortal = 'cliente' | 'publico' | 'outro';
export type SituacaoPortal = 'ativo' | 'pendente' | 'inativo';

export interface Portal {
  id: number;
  nome: string;
  url: string | null;
  tipo: TipoPortal;
  cliente_id: number | null;
  cliente_nome: string | null;
  usuario_login: string | null;
  situacao: SituacaoPortal;
  validade: string | null;
  responsavel_email: string | null;
  observacao: string | null;
  notion_page_id: string | null;
  /** true quando há senha guardada no cofre. O id do segredo NÃO vem. */
  tem_senha: boolean;
  /** `situacao`, ou 'vencido'/'vencendo' quando a validade manda. Derivado no
   *  servidor: guardar 'vencido' numa coluna criaria duas verdades. */
  status: SituacaoPortal | 'vencido' | 'vencendo';
  revelado_em: string | null;
  criado_por: string;
  criado_em: string;
  atualizado_em: string | null;
  atualizado_por: string | null;
}

export interface AcessoPortal {
  id: number;
  segredo_id: number | null;
  rotulo_snap: string;
  usuario_email: string;
  acao: 'criou' | 'alterou' | 'revelou' | 'baixou_arquivo' | 'excluiu';
  detalhe: string | null;
  em: string;
}

async function chamar<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
    { action, ...body }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export const listarPortais = (f: { tipo?: string; busca?: string } = {}) =>
  chamar<{ portais: Portal[]; pode_revelar: boolean; pode_gerenciar: boolean }>('por_listar', f);

/** Devolve a senha em texto UMA vez. A chamada é registrada em cofre.acessos
 *  ANTES de responder — se a trilha não gravar, a senha não sai. */
export const revelarSenha = (id: number, motivo?: string) =>
  chamar<{ valor: string; nome: string }>('por_revelar', { id, motivo });

/** Cria ou edita. `senha` é opcional: em branco na edição, o valor guardado
 *  não é tocado. O servidor ignora `segredo_id` se alguém mandar. */
/** `aviso` presente = sucesso PARCIAL: o cadastro entrou, a senha não. Vem como
 *  200 de propósito — com erro, o `id` do portal recém-criado se perderia e o
 *  segundo clique em Salvar criaria um cadastro duplicado. */
export const salvarPortal = (p: Record<string, any>) =>
  chamar<{ id: number; criado?: boolean; atualizado?: boolean; tem_senha?: boolean;
           senha_trocada?: boolean; aviso?: string }>('por_salvar', p);

export const excluirPortal = (id: number, motivo?: string) =>
  chamar<{ excluido: boolean; senha_removida: boolean }>('por_excluir', { id, motivo });

export const listarAcessosPortais = (f: { portal_id?: number; limite?: number } = {}) =>
  chamar<{ acessos: AcessoPortal[] }>('por_acessos', f);

/** Uma pessoa na lista de quem pode revelar. Revogado NÃO some da lista —
 *  `ativo: false` com carimbo, porque "quem tirou o Fulano, e quando" é a
 *  pergunta que se faz depois de um incidente. */
export interface AcessoPessoa {
  id: number;
  usuario_email: string;
  nome_snap: string | null;
  ativo: boolean;
  concedido_por: string;
  concedido_em: string;
  revogado_por: string | null;
  revogado_em: string | null;
  observacao: string | null;
}

/** 🚨 Esta lista É a permissão de revelar. Não existe `comercial.portais.revelar`
 *  na matriz do Admin — foi aposentada, porque permissão de PERFIL liberava o
 *  cargo inteiro quando se queria liberar uma pessoa. */
export const listarAcessoPortais = () =>
  chamar<{ acesso: AcessoPessoa[]; pode_gerenciar_acesso: boolean; eu: string }>('por_acesso_listar');

export const definirAcessoPortal = (usuario_email: string, pode: boolean, nome?: string, motivo?: string) =>
  chamar<{ usuario_email: string; pode: boolean }>('por_acesso_definir',
    { usuario_email, pode, nome, motivo });

export const TIPO_PORTAL: Record<TipoPortal, { label: string; cor: string }> = {
  cliente: { label: 'Portal de cliente', cor: 'blue' },
  publico: { label: 'Licitação pública', cor: 'green' },
  outro:   { label: 'Outro',             cor: 'default' },
};

export const STATUS_PORTAL: Record<string, { label: string; cor: string }> = {
  ativo:    { label: 'Ativo',            cor: 'green' },
  pendente: { label: 'Cadastro pendente', cor: 'orange' },
  inativo:  { label: 'Inativo',          cor: 'default' },
  vencendo: { label: 'Vencendo',         cor: 'orange' },
  vencido:  { label: 'Vencido',          cor: 'red' },
};
